import React from "react";
import { useTranslation } from "react-i18next";
import { PropTypes } from "prop-types";

// Material UI
import { Box, Divider, Typography } from "@mui/material";
import { Tooltip } from "../Tooltip";
import { useTheme } from "@mui/styles";

// Custom Components
import PieChart from "../PieChart/PieChart";
import ProgressBar from "../ProgressBar/ProgressBar";
import Skeleton from "../Skeleton/Skeleton";

const AdministrationInfoLayout = ({
  name,
  image,
  subtitle1,
  subtitle2,
  compliance,
  completedTraining,
  trainingHours,
  averageScore,
  averages,
  infoSection,
  roles,
  tables,
  actions,
  isLoadingInfo,
  isLoadingRoles,
  infoActions
}) => {
  const theme = useTheme();
  const { t } = useTranslation();

  return (
    <Box display="flex" flexWrap="wrap">
      <Box flex="1 0 100%" mb={3}>
        <Skeleton height={188} width="100%" loading={isLoadingInfo}>
          <Box
            display="flex"
            alignItems="center"
            bgcolor={theme.palette.secondary[50]}
            flexWrap="wrap"
            px={1}
          >
            <Box display="flex" justifyContent="center" flex={1} p={4}>
              {image}
            </Box>
            <Box
              display="flex"
              flexDirection="column"
              px={4}
              py={2}
              flex={9999}
              textAlign={{ xs: "center", md: "left" }}
            >
              <Typography
                variant="h3"
                sx={{ pb: 0.75 }}
                data-qa="administration-info-name"
              >
                {name}
              </Typography>
              <Typography
                variant="subtitle1"
                color="textSecondary"
                sx={{ pb: 0.75 }}
              >
                {subtitle1}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {subtitle2}
              </Typography>
            </Box>
            {infoActions && (
              <Box
                p={4}
                alignSelf="flex-end"
                display="flex"
                justifyContent={{ xs: "center", sm: "flex-end" }}
                flex={1}
              >
                {infoActions}
              </Box>
            )}
          </Box>
        </Skeleton>
      </Box>
      {infoSection && (
        <Skeleton height={200} loading={isLoadingInfo} width="100%" mb={3}>
          <Box
            bgcolor={theme.palette.secondary[50]}
            display="flex"
            flex="1 0 100%"
            p={{ xs: 3, md: 5 }}
            alignItems="flex-start"
            mb={3}
            flexWrap="wrap"
          >
            {infoSection}
          </Box>
        </Skeleton>
      )}
      <Tooltip title={t("administration.complianceTooltip")}>
        <Box
          ml={5}
          mr={6.25}
          display="flex"
          justifyContent="center"
          flex={1}
          mb={5}
        >
          <Box
            display="flex"
            flexDirection="column"
            width={144}
            alignItems="center"
          >
            <Typography variant="h6" sx={{ pb: 2.25 }}>
              {t("administration.compliance")}
            </Typography>
            <Skeleton
              variant="circular"
              loading={isLoadingInfo}
              height={144}
              width={144}
            >
              <PieChart percent={parseInt(compliance)} color="primary" />
            </Skeleton>
          </Box>
        </Box>
      </Tooltip>
      <Box
        display="flex"
        flexDirection="column"
        flexGrow={9999}
        mr={{ xs: 0, md: 8 }}
        mb={4}
      >
        <Skeleton height={42} minWidth={240} loading={isLoadingInfo} mb={2}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Tooltip title={t("administration.completedTrainingTooltip")}>
              <Typography variant="h6" sx={{ pr: 3 }} noWrap>
                {t("administration.completedTraining")}
              </Typography>
            </Tooltip>
            <Typography
              variant="h6"
              data-qa="administration-info-completed-training"
            >
              {completedTraining}
            </Typography>
          </Box>
          <Divider sx={{ my: 1.5 }} />
        </Skeleton>
        <Skeleton height={42} minWidth={240} loading={isLoadingInfo} mb={2}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Tooltip title={t("administration.totalTrainingHoursTooltip")}>
              <Typography variant="h6" sx={{ pr: 3 }} noWrap>
                {t("administration.totalTrainingHours")}
              </Typography>
            </Tooltip>
            <Typography
              variant="h6"
              data-qa="administration-info-training-hours"
            >
              {trainingHours}
            </Typography>
          </Box>
          <Divider sx={{ my: 1.5 }} />
        </Skeleton>
        <Skeleton height={42} minWidth={240} loading={isLoadingInfo} mb={2}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Tooltip title={t("administration.avgScoreTooltip")}>
              <Typography variant="h6" sx={{ pr: 3 }} noWrap>
                {t("administration.avgScore")}
              </Typography>
            </Tooltip>
            <Typography
              variant="h6"
              data-qa="administration-info-average-score"
            >
              {averageScore}%
            </Typography>
          </Box>
          <ProgressBar
            completedSteps={averageScore}
            totalSteps={100}
            height={24}
          />
        </Skeleton>
      </Box>
      <Box flexGrow={9999} pr={3} mb={4}>
        {averages}
      </Box>
      {roles && (
        <Skeleton
          height={288}
          loading={isLoadingInfo || isLoadingRoles}
          width="100%"
          mb={3}
        >
          <Box
            flex="1 0 100%"
            flexDirection="column"
            width="100%"
            bgcolor={theme.palette.secondary[50]}
            px={{ xs: 3, md: 5 }}
            pb={{ xs: 3, md: 5 }}
            pt={{ xs: 1, md: 3 }}
            mb={3}
          >
            {roles}
          </Box>
        </Skeleton>
      )}
      {tables &&
        tables.map((tableData, index) => (
          <Box
            display="flex"
            flexDirection="column"
            flex="1 0 100%"
            mb={4}
            maxWidth="100%"
            key={index}
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              pb={1.5}
            >
              <Typography variant="h6">{tableData.header}</Typography>
              {tableData.actions && <Box>{tableData.actions}</Box>}
            </Box>
            {tableData.table}
          </Box>
        ))}
      {actions && (
        <Box display="flex" justifyContent="flex-end" flex={1}>
          {actions}
        </Box>
      )}
    </Box>
  );
};

export default AdministrationInfoLayout;

AdministrationInfoLayout.propTypes = {
  name: PropTypes.string,
  image: PropTypes.node,
  subtitle1: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  subtitle2: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  compliance: PropTypes.number,
  completedTraining: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  trainingHours: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  averageScore: PropTypes.number,
  averages: PropTypes.node,
  infoSection: PropTypes.node,
  roles: PropTypes.node,
  tables: PropTypes.arrayOf(
    PropTypes.shape({
      header: PropTypes.string,
      table: PropTypes.node
    })
  ),
  actions: PropTypes.node,
  isLoadingInfo: PropTypes.bool,
  isLoadingRoles: PropTypes.bool
};
